import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack } from '@mui/material';
import { Simulation } from '@thive/fddm-types';

import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { Status } from '@/modules/simulation/components/Status';
import { TopButtons } from '@/modules/simulation/components/TopButtons';
import { SimulationServiceFactory } from '@/modules/simulation/service/SimulationServiceFactory';
import { SimulationUtil } from '@/modules/simulation/SimulationUtil';

type Props = {
  simulation: Simulation;
  isHover: boolean;
};

export const CardHeader = ({ simulation, isHover }: Props) => {
  const navigate = useNavigate();
  const simSvc = useMemo(
    () => SimulationServiceFactory.getInstance().createSimulationService(),
    [],
  );

  const goToTracker = useCallback(
    (isBeta: boolean) => {
      const floorPlanMetaData = useFloorplanDesignStore.getState().metaData;

      if (!simulation.floorPlanRevisionId) {
        return;
      }

      const url = SimulationUtil.createFleetTrackerUrl(
        floorPlanMetaData.projectId,
        floorPlanMetaData.alternativeId,
        floorPlanMetaData.revisionId,
        simulation.simulationRunId,
        simulation.floorPlanRevisionId,
        isBeta,
      );
      navigate(url);
    },
    [simulation.simulationRunId, simulation.floorPlanRevisionId, navigate],
  );

  const onDownloadSimulationFilesClick = useCallback(() => {
    if (simulation.simulationRunId) {
      simSvc.downloadSimulationFiles(simulation.simulationRunId, simulation.name);
    }
  }, [simulation.simulationRunId, simulation.name, simSvc]);

  return (
    <Stack
      sx={{
        alignItems: 'center',
        flexDirection: 'row',
        gap: 1,
        mb: 1,
      }}
    >
      <Status status={simulation.status} isGroup={false} />
      <Stack
        sx={{ alignItems: 'center', flexDirection: 'row', ml: 'auto', gap: 2, height: '32px' }}
      >
        {isHover && (
          <TopButtons
            status={simulation.status}
            simulationRunId={simulation.simulationRunId}
            dashboardUrl={simulation.dashboardUrl}
            dashboardUrlExpirationDate={
              simulation.dashboardUrlExpirationDate
                ? new Date(simulation.dashboardUrlExpirationDate)
                : null
            }
            onViewClick={() => goToTracker(true)}
            onDownloadSimulationFilesClick={() => onDownloadSimulationFilesClick()}
          />
        )}
      </Stack>
    </Stack>
  );
};
